import {
  EMBEDDED_BASE_URL,
  ENVELOPE_SOURCE,
  ENVELOPE_VERSION,
} from "./constants"
import type { EmbeddedEventName, EmbeddedEventPayloads } from "./types"

type Handler<E extends EmbeddedEventName> = (
  payload: EmbeddedEventPayloads[E],
) => void

export interface ParentBridge {
  on<E extends EmbeddedEventName>(event: E, handler: Handler<E>): () => void
  emit(command: "close"): void
  getLastImageUrl(): string | null
  dispose(): void
}

export function createParentBridge(
  iframe: HTMLIFrameElement,
  widgetOrigin: string = EMBEDDED_BASE_URL,
): ParentBridge {
  const handlers = new Map<EmbeddedEventName, Set<Handler<EmbeddedEventName>>>()
  let lastImageUrl: string | null = null

  const onMessage = (event: MessageEvent) => {
    if (event.origin !== widgetOrigin) return
    if (event.source !== iframe.contentWindow) return
    if (event.data?.source !== ENVELOPE_SOURCE) return
    if (event.data?.version !== ENVELOPE_VERSION) return

    const name = event.data.event as EmbeddedEventName
    const payload = event.data.payload ?? {}

    if (name === "image_saved" && typeof payload?.imageUrl === "string") {
      lastImageUrl = payload.imageUrl
    }

    const set = handlers.get(name)
    if (set) {
      for (const h of set) {
        try {
          h(payload)
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[PalazzoSDK] handler threw", err)
        }
      }
    }
  }

  window.addEventListener("message", onMessage)

  return {
    on(event, handler) {
      if (!handlers.has(event)) handlers.set(event, new Set())
      const set = handlers.get(event)!
      set.add(handler as Handler<EmbeddedEventName>)
      return () => {
        set.delete(handler as Handler<EmbeddedEventName>)
      }
    },
    emit(command) {
      iframe.contentWindow?.postMessage(
        {
          source: ENVELOPE_SOURCE,
          version: ENVELOPE_VERSION,
          event: command,
          payload: {},
        },
        widgetOrigin,
      )
    },
    getLastImageUrl: () => lastImageUrl,
    dispose: () => {
      window.removeEventListener("message", onMessage)
      handlers.clear()
    },
  }
}
